/**
 * Single source of truth for "what audiences does this topic serve, and
 * with what fit strength + trust?"
 *
 * The resolution chain handles all 5 cases uniformly:
 *
 *   1. topic.audienceFit[] non-empty               → use that
 *   2. topic.appliesToAllAudiences === true        → synthesize medium/user_confirmed
 *                                                     fits for each selected ref
 *   3. topic.targetAudienceIds[] legacy fallback    → synthesize medium/ai_verified
 *   4. theme.audienceFit[] non-empty across themes → deduped union
 *   5. any theme.appliesToAllAudiences === true    → synthesize universal fits
 *   6. otherwise                                    → empty array (unscoped — B2B invalid)
 *
 * Used by: filterEligibleTopics (gate), audienceCoverageValidator
 * (validation), SprintPlannerHandler.renderPlannerCandidate (ranking),
 * topic detail UI (badge rendering), strategy review screen.
 *
 * IMPORTANT: topic.themeAssignment[] stores `{ themeId, weight }` — NOT
 * embedded theme objects. The resolver REQUIRES a themeCatalog map for
 * theme inheritance; without it the chain breaks at the theme step.
 */

import type {
  ContentTopic,
  ContentTheme,
  AudienceFit,
  AudienceBriefRef,
} from '@segmently/schema-models/content-plan';
import { resolveThemeId } from './themeKeys';

export interface ResolveAudienceFitsInput {
  topic: Pick<
    ContentTopic,
    'audienceFit' | 'appliesToAllAudiences' | 'targetAudienceIds' | 'themeAssignment'
  >;
  /** themeId → ContentTheme lookup. Loaded once per planner run / UI render. */
  themeCatalog: Record<string, ContentTheme>;
  /**
   * Optional — refs selected for the parent strategy. Required when a
   * universal topic/theme needs to synthesize concrete fits.
   */
  selectedAudienceBriefRefs?: AudienceBriefRef[];
}

const SYNTHETIC_MEDIUM_USER_CONFIRMED: Pick<AudienceFit, 'fit' | 'source'> = {
  fit: 'medium',
  source: 'user_confirmed',
};

function synthesizeUniversalFits(
  refs: AudienceBriefRef[] | undefined,
  rationale: string,
): AudienceFit[] {
  if (!refs || refs.length === 0) return [];
  return refs.map(
    (ref): AudienceFit => ({
      audienceBriefRef: ref,
      ...SYNTHETIC_MEDIUM_USER_CONFIRMED,
      rationale,
    }),
  );
}

function dedupeFitsByAudienceId(fits: AudienceFit[]): AudienceFit[] {
  const seen = new Map<string, AudienceFit>();
  for (const fit of fits) {
    const key = fit.audienceBriefRef?.audienceId ?? '';
    if (!key) continue;
    if (!seen.has(key)) seen.set(key, fit);
  }
  return Array.from(seen.values());
}

function synthesizeLegacyTargetAudienceFits(
  audienceIds: string[] | undefined,
  refs: AudienceBriefRef[] | undefined,
): AudienceFit[] {
  if (!audienceIds || audienceIds.length === 0) return [];
  return Array.from(new Set(audienceIds.filter(Boolean))).map((audienceId) => {
    const ref = refs?.find((candidate) => candidate.audienceId === audienceId) ?? {
      analysisId: '',
      audienceId,
      sourcePath: `@audience[${audienceId}]`,
    };
    return {
      audienceBriefRef: ref,
      fit: 'medium',
      source: 'ai_verified',
      rationale: 'Inherited from legacy topic.targetAudienceIds[]',
    } satisfies AudienceFit;
  });
}

export function resolveAudienceFits(
  input: ResolveAudienceFitsInput,
): AudienceFit[] {
  const { topic, themeCatalog, selectedAudienceBriefRefs } = input;

  // 1. Topic-level explicit fits win.
  const topicFits = topic.audienceFit ?? [];
  if (topicFits.length > 0) return topicFits;

  // 2. Topic-level universal flag → synthesize fits for selected refs.
  if (topic.appliesToAllAudiences === true) {
    return synthesizeUniversalFits(
      selectedAudienceBriefRefs,
      'Inherited from topic.appliesToAllAudiences=true',
    );
  }

  // 3. Legacy compatibility: older generated topics may only carry
  // targetAudienceIds[]. New writes should prefer audienceFit[], but the
  // planner gate must still understand already persisted backlog items.
  const legacyTargetFits = synthesizeLegacyTargetAudienceFits(
    topic.targetAudienceIds,
    selectedAudienceBriefRefs,
  );
  if (legacyTargetFits.length > 0) return legacyTargetFits;

  // 4. Theme-level fits — deduped union.
  const themes: ContentTheme[] = (topic.themeAssignment ?? [])
    .map((assignment) => themeCatalog[resolveThemeId(assignment)])
    .filter((theme): theme is ContentTheme => Boolean(theme));

  const themeFits = themes.flatMap((t) => t.audienceFit ?? []);
  if (themeFits.length > 0) return dedupeFitsByAudienceId(themeFits);

  // 5. Any theme universal → synthesize.
  if (themes.some((t) => t.appliesToAllAudiences === true)) {
    return synthesizeUniversalFits(
      selectedAudienceBriefRefs,
      'Inherited from theme.appliesToAllAudiences=true',
    );
  }

  // 6. Unscoped — B2B validator rejects.
  return [];
}

/**
 * Convenience predicate for sprint planner / validator:
 *   "does this topic cover the sprint's primary audience?"
 *
 * Returns true if:
 *   - topic.appliesToAllAudiences === true (universal topic fits any lane), OR
 *   - any resolved fit's audienceBriefRef.audienceId === audienceId
 */
export function topicCoversAudience(
  topic: ResolveAudienceFitsInput['topic'],
  audienceId: string,
  themeCatalog: Record<string, ContentTheme>,
  selectedAudienceBriefRefs?: AudienceBriefRef[],
): boolean {
  if (topic.appliesToAllAudiences === true) return true;
  const fits = resolveAudienceFits({
    topic,
    themeCatalog,
    selectedAudienceBriefRefs,
  });
  return fits.some((f) => f.audienceBriefRef?.audienceId === audienceId);
}

/**
 * Computed audience scope used by UI badges and the sprint planner gate
 * for ranking signals. NOT stored — derived on every read.
 */
export type AudienceScope = 'single' | 'multi' | 'all_selected' | 'unscoped';

export function computeAudienceScope(
  topic: ResolveAudienceFitsInput['topic'],
): AudienceScope {
  if (topic.appliesToAllAudiences === true) return 'all_selected';
  const fits = topic.audienceFit ?? [];
  if (fits.length === 1) return 'single';
  if (fits.length > 1) return 'multi';
  const legacyTargetAudienceIds = topic.targetAudienceIds ?? [];
  if (legacyTargetAudienceIds.length === 1) return 'single';
  if (legacyTargetAudienceIds.length > 1) return 'multi';
  return 'unscoped';
}
